import { useEffect, useState } from "react";
import * as Speech from "expo-speech";

import { pickVoice, SPEECH_RATE, type SpeechVoice } from "../speech";
import type { ProtocolLocale } from "../../injection-protocol/types";

/**
 * Reads the current step aloud, and stops the moment it should.
 *
 * Speaking is the half of the coach that works with a needle already in hand:
 * she cannot look up, and the instruction is on a screen she is not facing.
 *
 * The words never leave the device — but that is a property of `pickVoice`
 * refusing any voice that reports itself remote, not of the engine being local
 * by nature. Several platforms ship cloud voices in the same list as their own,
 * and this hook once preferred them, which uploaded her brand and her dose.
 * See `isRemoteVoice`.
 *
 * Everything difficult here is about stopping rather than starting.
 *
 * - **One utterance at a time.** `Speech.speak` queues, so advancing three steps
 *   quickly would otherwise read all three in turn while she is already past
 *   them. Each new step stops what is speaking before it begins.
 * - **`stop()` is asynchronous, and the step can change while it settles.** A
 *   cancellation flag is what stops the previous step's `speak` landing after
 *   the next one has already started — the bug that reads the injection step
 *   aloud during aftercare.
 * - **Silence on the way out.** Unmounting, turning it off, pausing on a
 *   warning: all of them stop the voice. A coach that keeps talking after she
 *   has been told to call her clinic is worse than one that never spoke.
 *
 * It also asks for a specific voice rather than accepting the engine's default,
 * which on every platform is the smallest one installed — see `pickVoice`. That
 * lookup is resolved *before* the first utterance rather than applied when it
 * arrives: letting it land afterwards restarts the sentence she is already
 * listening to, in a different voice, which is worse than the compact one.
 */

type VoiceLookup = {
  /** Whether the device has been asked. Nothing is spoken before it has. */
  settled: boolean;
  /** Null means the engine picks — no list, or nothing suitable in it. */
  voice: SpeechVoice | null;
};

export function useStepSpeech({
  enabled,
  text,
  language,
  locale,
}: {
  enabled: boolean;
  /** Already normalised by `speakableStepText`. Null means say nothing. */
  text: string | null;
  /** BCP 47, from `speechLanguage`. */
  language: string;
  locale: ProtocolLocale;
}) {
  const [lookup, setLookup] = useState<VoiceLookup>({
    settled: false,
    voice: null,
  });

  /**
   * Asked once, when the voice is first switched on.
   *
   * Not on mount, because the list is an operating-system call and most walks
   * never turn the voice on. Not per step either — the voice must not change
   * halfway through a procedure, which reads as a second speaker arriving.
   */
  useEffect(() => {
    if (!enabled || lookup.settled) return;
    let cancelled = false;

    // The web implementation resolves only inside `onvoiceschanged` when the
    // initial list is empty, and never rejects. On a browser where that event
    // does not fire, nothing would ever settle — and since the first utterance
    // waits for it, the toggle would read "Stop reading" over permanent
    // silence. A timeout settles with no preference, which is what the engine
    // did before any of this existed.
    const giveUp = setTimeout(() => {
      if (!cancelled) setLookup({ settled: true, voice: null });
    }, 2000);

    void Speech.getAvailableVoicesAsync()
      .then((available) => {
        if (cancelled) return;
        setLookup({ settled: true, voice: pickVoice(available ?? [], locale) });
      })
      .catch(() => {
        // No voice list is not a failure: the engine picks, as it did before
        // this existed. Settling anyway is what lets the step be spoken at all.
        if (!cancelled) setLookup({ settled: true, voice: null });
      });

    return () => {
      cancelled = true;
      clearTimeout(giveUp);
    };
  }, [enabled, locale, lookup.settled]);

  useEffect(() => {
    let cancelled = false;

    // `!lookup.settled` holds the first utterance for the length of one
    // platform call, so the good voice speaks the first step rather than
    // interrupting it.
    if (!enabled || !text || !lookup.settled) {
      void Speech.stop();
      return () => {
        cancelled = true;
        void Speech.stop();
      };
    }

    const voice = lookup.voice;

    void Speech.stop()
      .then(() => {
        if (cancelled) return;
        Speech.speak(text, {
          language,
          rate: SPEECH_RATE,
          // Omitted rather than passed as undefined when nothing was chosen, so
          // the engine falls back to its own default instead of being handed a
          // voice identifier it cannot resolve.
          ...(voice ? { voice: voice.identifier } : {}),
        });
      })
      .catch(() => {
        // A device with no speech engine, or a browser that refuses without a
        // user gesture. The step is on screen either way, so this is a missing
        // convenience rather than a failure worth telling her about.
      });

    return () => {
      cancelled = true;
      void Speech.stop();
    };
  }, [enabled, text, language, lookup.settled, lookup.voice]);
}
